import { useState } from 'react';
import { useDeepSignal } from '../context';
import { CHART_MAX } from '../constants';
import { buildVariableScoresFromApi } from '../builders';
import { getModelLanes } from '../model-lanes';
import { SectionLabel, SectionTitle, DSLoadingRow } from './section-primitives';

// Natural-language join: ["A","B","C"] -> "A, B, and C"
function joinModelNames(names: string[]): string {
  if (names.length === 0) return 'the tracked models';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function VariableScoreChart() {
  const { apiData, loading } = useDeepSignal();
  // Which variable's full definition is open (by name); labels are short
  // summary ideas, the whole claim shows on click.
  const [openDef, setOpenDef] = useState<string | null>(null);
  if (loading) return (
    <div style={{ borderBottom: '1px solid var(--border)', padding: '32px 64px' }}>
      {[1,2,3,4,5,6,7].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData) return null;
  const scores = buildVariableScoresFromApi(apiData);
  const modelList = joinModelNames(getModelLanes(Object.keys(apiData.model_breakdown)).map((l) => l.label));
  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      backgroundColor: 'var(--background)',
      padding: '32px 64px 40px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <SectionLabel>Variable Intelligence</SectionLabel>
        <SectionTitle size="xl">How well are those attributes understood across models?</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginTop: '4px' }}>
          Averaged across {modelList} · Scale 0 to 100
        </p>
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {scores.map((v) => {
          const value = (v.score / CHART_MAX) * 100;    // literal 0 to 100; bar fills against the 0-100 ruler
          const isLeader = v.encoded;
          const hasDef = Boolean(v.definition);
          const defOpen = openDef === v.name;

          return (
            <div
              key={v.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '340px 1fr 56px',
                alignItems: 'center',
                gap: '16px',
                padding: '8px 0',
                borderBottom: '1px solid var(--border)',
              }}
            >
              {/* Variable label box: a short summary idea, uniform weight
                  (the encoded distinction is the labeled chip, never bold).
                  Click to open the full claim, which wraps INSIDE this box,
                  never under the bar. */}
              <div style={{ minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    onClick={hasDef ? () => setOpenDef(defOpen ? null : v.name) : undefined}
                    title={hasDef ? v.definition : v.name}
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 500,
                      color: 'var(--foreground)',
                      lineHeight: 1.4,
                      cursor: hasDef ? 'pointer' : 'default',
                    }}
                  >
                    {v.name}
                    {hasDef && (
                      <span style={{
                        marginLeft: '5px',
                        display: 'inline-block',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--muted-foreground)',
                        transition: 'transform 0.15s',
                        transform: defOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}>›</span>
                    )}
                  </span>
                  {isLeader && (
                    <span style={{
                      flexShrink: 0,
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase' as const,
                      color: '#00c9c4',
                      backgroundColor: 'rgba(0, 201, 196, 0.12)',
                      border: '1px solid rgba(0, 201, 196, 0.35)',
                      borderRadius: '99px',
                      padding: '1px 7px',
                    }}>
                      Encoded
                    </span>
                  )}
                </span>
                {defOpen && hasDef && (
                  <p style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                    lineHeight: 1.55,
                    margin: '4px 0 2px',
                  }}>
                    {v.definition}
                  </p>
                )}
              </div>

              {/* Bar on a 0-100 ruler: faint gridlines mark the scale, bar fills to its true value */}
              <div style={{ position: 'relative', height: '10px' }}>
                {[25, 50, 75, 100].map((t) => (
                  <div key={t} style={{ position: 'absolute', left: `${t}%`, top: -4, bottom: -4, width: '1px', backgroundColor: 'var(--border)' }} />
                ))}
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${value}%`,
                  borderRadius: '99px',
                  backgroundColor: '#00c9c4',
                }} />
              </div>

              {/* Score */}
              <span style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 700,
                color: '#00c9c4',
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {Math.round((v.score / CHART_MAX) * 100)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
